import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Popconfirm, Modal, Form, Input, Switch, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { fetchChannels, createChannel, updateChannel, deleteChannel, type ChannelDto } from '../services/channelService';
import { useCanDelete } from '../hooks/useCanDelete';

const Channels: React.FC = () => {
  const canDelete = useCanDelete();
  const [channels, setChannels] = useState<ChannelDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingChannel, setEditingChannel] = useState<ChannelDto | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadChannels();
  }, []);

  const loadChannels = async () => {
    try {
      setLoading(true);
      const data = await fetchChannels();
      setChannels(data);
    } catch (error) {
      message.error('Kanal verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingChannel(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: ChannelDto) => {
    setEditingChannel(record);
    form.setFieldsValue({
      name: record.name,
      isActive: record.isActive,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteChannel(id);
      message.success('Kanal başarıyla silindi');
      loadChannels();
    } catch (error) {
      message.error('Kanal silinirken hata oluştu');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingChannel) {
        await updateChannel(editingChannel.id, values);
        message.success('Kanal başarıyla güncellendi');
      } else {
        await createChannel(values);
        message.success('Kanal başarıyla oluşturuldu');
      }
      setModalVisible(false);
      loadChannels();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('İşlem sırasında hata oluştu');
      }
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Kanal Adı',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
    },
    {
      title: 'Oluşturulma',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString('tr-TR'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 150,
      render: (_: any, record: ChannelDto) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          {canDelete && (
          <Popconfirm
            title="Bu kanalı silmek istediğinize emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Sil
            </Button>
          </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Satış Kanalları</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Yeni Kanal Ekle
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={channels}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Toplam ${total} kayıt`,
        }}
      />

      <Modal
        title={editingChannel ? 'Kanal Düzenle' : 'Yeni Kanal Ekle'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Kaydet"
        cancelText="İptal"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Kanal Adı"
            rules={[{ required: true, message: 'Kanal adı zorunludur' }]}
          >
            <Input placeholder="Örn: Amazon, Trendyol, Shopify" />
          </Form.Item>
          <Form.Item
            name="isActive"
            label="Durum"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Channels;

